import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
import { DocumentSummarySchema } from "./documents.js";
const FOLDER_ACL_MODES = ["inherit", "custom"];
const FolderAclModeSchema = z.enum(FOLDER_ACL_MODES);
const FOLDER_PERMISSIONS = ["can_read", "can_write", "can_manage"];
const FolderPermissionSchema = z.enum(FOLDER_PERMISSIONS);
const FolderNameSchema = z.string().trim().min(1).max(200).regex(/^[^/\\\u0000-\u001f\u007f]+$/, "a folder name without / or \\").refine((name) => name !== "." && name !== "..", "a folder name other than . or ..");
const FolderSchema = z.strictObject({
  id: IdSchema,
  spaceId: IdSchema,
  parentId: IdSchema.nullable(),
  name: z.string(),
  depth: z.int().min(0),
  aclMode: FolderAclModeSchema,
  /** Live subfolders exist (the caller may still not read them). */
  hasChildren: z.boolean(),
  createdAt: IsoDateTimeSchema,
  version: z.int().min(1),
  _permissions: z.array(FolderPermissionSchema)
});
const FolderListQuerySchema = z.strictObject({ parentId: IdSchema.optional() });
const FolderListSchema = z.strictObject({ items: z.array(FolderSchema) });
const FolderDetailSchema = FolderSchema.extend({
  /** Ancestors from the root, those the caller cannot read shown without a name. */
  breadcrumbs: z.array(z.strictObject({ id: IdSchema, name: z.string().nullable() }))
});
const CreateFolderRequestSchema = z.strictObject({
  parentId: IdSchema.nullable(),
  name: FolderNameSchema
});
const UpdateFolderRequestSchema = z.strictObject({ name: FolderNameSchema, parentId: IdSchema.nullable() }).partial().refine((p) => Object.keys(p).length > 0, { message: "nothing to update" });
const SpaceParamsSchema = z.strictObject({ spaceId: IdSchema });
const FolderParamsSchema = z.strictObject({ spaceId: IdSchema, folderId: IdSchema });
const FolderItemsQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(200).default(100),
  cursor: z.string().min(1).max(512).optional()
});
const FolderItemSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("folder"), folder: FolderSchema }),
  z.strictObject({ kind: z.literal("document"), document: DocumentSummarySchema })
]);
const FolderItemsSchema = z.strictObject({
  items: z.array(FolderItemSchema),
  nextCursor: z.string().nullable()
});
const FOLDER_ACL_RELATIONS = ["reader", "writer"];
const FolderAclRelationSchema = z.enum(FOLDER_ACL_RELATIONS);
const FOLDER_ACL_SUBJECTS = ["user", "organization_members"];
const FolderAclSubjectSchema = z.enum(FOLDER_ACL_SUBJECTS);
const FolderAclEntrySchema = z.strictObject({
  relation: FolderAclRelationSchema,
  subjectType: FolderAclSubjectSchema,
  subjectId: IdSchema
});
const FolderAclSchema = z.strictObject({
  mode: FolderAclModeSchema,
  entries: z.array(FolderAclEntrySchema.extend({ displayName: z.string() })),
  version: z.int().min(1)
});
const PutFolderAclRequestSchema = z.strictObject({
  mode: FolderAclModeSchema,
  entries: z.array(FolderAclEntrySchema).max(200).default([])
}).superRefine((body, ctx) => {
  if (body.mode === "inherit" && body.entries.length > 0)
    ctx.addIssue({
      code: "custom",
      path: ["entries"],
      message: "an inherited ACL has no entries"
    });
  const keys = body.entries.map((e) => `${e.subjectType}:${e.subjectId}`);
  if (new Set(keys).size !== keys.length)
    ctx.addIssue({ code: "custom", path: ["entries"], message: "a subject is listed twice" });
});
export {
  CreateFolderRequestSchema,
  FOLDER_ACL_MODES,
  FOLDER_ACL_RELATIONS,
  FOLDER_ACL_SUBJECTS,
  FOLDER_PERMISSIONS,
  FolderAclEntrySchema,
  FolderAclModeSchema,
  FolderAclRelationSchema,
  FolderAclSchema,
  FolderAclSubjectSchema,
  FolderDetailSchema,
  FolderItemSchema,
  FolderItemsQuerySchema,
  FolderItemsSchema,
  FolderListQuerySchema,
  FolderListSchema,
  FolderNameSchema,
  FolderParamsSchema,
  FolderPermissionSchema,
  FolderSchema,
  PutFolderAclRequestSchema,
  SpaceParamsSchema,
  UpdateFolderRequestSchema
};
//# sourceMappingURL=folders.js.map